import { useState, type ReactNode } from "react";
import { Alert, Stack, Typography } from "@mui/joy";
import AdmissionShell from "./AdmissionShell";
import { AccessRecoverySwitchProject } from "./access/AccessRecoverySwitchProject";
import EmptyState from "./documents/common/EmptyState";
import { Quiet } from "./documents/design/Quiet";
import {
    clearProjectRefusal,
    useProjectRefusal,
    type ProjectRefusal,
} from "../contexts/projectRefusal";
import { useLogoutWithoutRouter } from "../hooks/useLogout";
import { usePlatform } from "../platform/PlatformProvider";

const COPY: Record<ProjectRefusal, { title: string; hint: string }> = {
    project_inactive: {
        title: "This project is archived",
        hint: "Contact your Primepoint admin if you still need access.",
    },
    tenant_not_provisioned: {
        title: "This project is not set up in Primepoint",
        hint: "Contact your Primepoint admin to set it up.",
    },
};

/** Replaces the app with `refused` once the API refuses this project for good (archived, or
 * not set up); without it every screen shows its own failure for the same reason, and the
 * access screen offers a Retry that cannot succeed. */
export default function ProjectRefusalGate({
    refused,
    children,
}: {
    refused: ReactNode;
    children: ReactNode;
}) {
    return <>{useProjectRefusal() ? refused : children}</>;
}

/** Hosts whose app owns admission (web, iOS): the bootstrap shell's Home and Sign out, and for a
 * project that is not set up, the switch to another one. */
export function RefusedProjectPage() {
    const refusal = useProjectRefusal();
    if (!refusal) return null;
    return (
        <AdmissionShell>
            <Stack sx={{ maxWidth: 520, mx: "auto", mt: "12vh", px: 3 }} spacing={1}>
                <Typography level="h2">{COPY[refusal].title}</Typography>
                <Typography>{COPY[refusal].hint}</Typography>
                {refusal === "tenant_not_provisioned" && <AccessRecoverySwitchProject />}
            </Stack>
        </AdmissionShell>
    );
}

/** Hosts that own sign-in and project selection (desktop) mount their shell only under
 * an admitted project, so this sits where their "Project access unavailable" state
 * does. Sign out is the one every host runs (`useLogout`), as the account menu's is; the
 * host's signed-out shell then takes over. */
export function HostRefusedProjectNotice() {
    const refusal = useProjectRefusal();
    const hostSignsIn = !!usePlatform().auth.logout;
    const logout = useLogoutWithoutRouter();
    const [pending, setPending] = useState(false);
    const [failed, setFailed] = useState(false);
    if (!refusal) return null;
    const signOut = async () => {
        setPending(true);
        setFailed(false);
        try {
            await logout();
            clearProjectRefusal();
        } catch {
            setFailed(true);
            setPending(false);
        }
    };
    return (
        <div role="alert">
            <EmptyState
                title={COPY[refusal].title}
                hint={COPY[refusal].hint}
                action={
                    hostSignsIn && (
                        <Stack gap={1}>
                            <Quiet disabled={pending} onClick={() => void signOut()}>
                                {pending ? "Signing out…" : "Sign out"}
                            </Quiet>
                            {failed && (
                                <Alert color="warning">Sign-out did not complete. Try again.</Alert>
                            )}
                        </Stack>
                    )
                }
            />
        </div>
    );
}
